import type { QueryClient } from "@tanstack/react-query";
import type { AnyRouter } from "@tanstack/react-router";
import { listImagesApiImagesGetQueryKey } from "../generated/api/@tanstack/react-query.gen";
import type { HydratedImage } from "../generated/api/types.gen";
import { trackEvent } from "./analytics";
import { api } from "./api-client";
import type { AsyncError } from "./errors";
import { toAsyncError } from "./errors";
import {
  invalidateGallerySessions,
  invalidateIterationUsage,
  invalidateProjectAssets,
} from "./queries";

interface BuildGenerationFormDataParams {
  prompt: string;
  sessionId?: string;
  projectId?: string;
  imageFiles?: (File | null)[] | null;
  /** Parallel to `imageFiles`. A slot with no file and an id is a saved asset. */
  referenceAssetIds?: (string | null)[] | null;
}

const REFERENCE_FILE_FIELDS = ["image", "reference_image_2"] as const;
const REFERENCE_ASSET_FIELDS = [
  "reference_asset_id",
  "reference_asset_id_2",
] as const;

export function buildGenerationFormData({
  prompt,
  sessionId,
  projectId,
  imageFiles,
  referenceAssetIds,
}: BuildGenerationFormDataParams): FormData {
  const formData = new FormData();
  formData.append("text", prompt);
  formData.append("is_refinement", "false");
  if (sessionId) formData.append("session_id", sessionId);
  // project_id is only sent on initial generation. Refinements reuse the
  // existing session, which already carries its project_id on the backend.
  if (projectId) formData.append("project_id", projectId);
  const slotCount = Math.min(
    REFERENCE_FILE_FIELDS.length,
    Math.max(imageFiles?.length ?? 0, referenceAssetIds?.length ?? 0),
  );
  for (let i = 0; i < slotCount; i++) {
    const file = imageFiles?.[i];
    if (file) {
      formData.append(REFERENCE_FILE_FIELDS[i], file);
      continue;
    }
    const assetId = referenceAssetIds?.[i];
    if (assetId) formData.append(REFERENCE_ASSET_FIELDS[i], assetId);
  }
  return formData;
}

interface ConsumeStreamParams {
  stream: AsyncIterable<HydratedImage>;
  requestId: string;
  queryClient: QueryClient;
  router: AnyRouter;
  setStreamError: (error: AsyncError) => void;
}

export async function consumeGenerationStream({
  stream,
  requestId,
  queryClient,
  router,
  setStreamError,
}: ConsumeStreamParams): Promise<void> {
  const newImages: HydratedImage[] = [];
  const imagesQueryKey = listImagesApiImagesGetQueryKey({
    query: { request_id: requestId },
  });

  try {
    for await (const event of stream) {
      newImages.push(event);
      queryClient.setQueryData(imagesQueryKey, {
        images: [...newImages],
        count: newImages.length,
      });
    }

    await invalidateGallerySessions(queryClient, router);
    // A newly generated design can carry an asset, which the project sidebar
    // shows as a derived-used asset; refresh it so it appears without a reload.
    await invalidateProjectAssets(queryClient);
    // This run spent an iteration; re-read the allowance so the header pill and
    // the out-of-iterations overlay reflect it immediately.
    await invalidateIterationUsage(queryClient);
  } catch (err) {
    const asyncErr = toAsyncError(err);
    // Re-read the allowance whenever this run could have spent an iteration:
    // a 403 is almost always a spent allowance the cache hadn't caught up
    // with, and a stream that yielded an image before failing was charged for
    // that image even though it errored. Either way the overlay must reflect
    // the true count rather than leave the UI enabled on a stale read.
    if (asyncErr?.status === 403 || newImages.length > 0) {
      await invalidateIterationUsage(queryClient);
    }
    if (asyncErr?.status === 403) {
      trackEvent("iteration_limit_hit", { flow: "initial" });
    }
    setStreamError(asyncErr);
  }
}

interface GenerateImagesParams {
  prompt: string | undefined;
  requestId: string;
  sessionId: string | undefined;
  projectId: string | undefined;
  imageFiles: (File | null)[] | null | undefined;
  referenceAssetIds?: (string | null)[] | null;
  queryClient: QueryClient;
  router: AnyRouter;
  setStreamError: (error: AsyncError) => void;
}

export async function generateImages({
  prompt,
  requestId,
  sessionId,
  projectId,
  imageFiles,
  referenceAssetIds,
  queryClient,
  router,
  setStreamError,
}: GenerateImagesParams): Promise<void> {
  setStreamError(null);
  if (!prompt) return;
  try {
    const generationRequestId = requestId || crypto.randomUUID();
    const formData = buildGenerationFormData({
      prompt,
      sessionId,
      projectId,
      imageFiles,
      referenceAssetIds,
    });
    const stream = api.generation.generateImageStream(
      formData,
      generationRequestId,
    );
    await consumeGenerationStream({
      stream,
      requestId: generationRequestId,
      queryClient,
      router,
      setStreamError,
    });
  } catch (err) {
    setStreamError(toAsyncError(err));
  }
}

export function shouldAutoTriggerGeneration(args: {
  hasImages: boolean;
  isFetched: boolean;
  lastTriggered: string | null;
  requestId: string;
}): boolean {
  return (
    !args.hasImages && args.isFetched && args.lastTriggered !== args.requestId
  );
}
